// Quick Help search (owner, 11 Oct: "Payments" found nothing, though the
// library has "How do I pay, and when am I charged?"). The old filter looked
// for the exact text typed. This matches words instead: their endings are
// trimmed ("payments" -> "pay"), a word matches the start of a longer one
// ("cancel" finds "cancellation"), and a few groups of words customers use
// for the same thing match each other ("payment" finds "paid" and "charged").

export interface HelpItem {
  id?: string;
  question: string;
  answer: string;
}

// Words that say nothing about the topic ("how do I pay" is about "pay").
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'can', 'do', 'does', 'for', 'get', 'how', 'i', 'in', 'is', 'it',
  'me', 'my', 'of', 'on', 'or', 'the', 'to', 'what', 'when', 'where', 'who', 'why', 'with', 'you', 'your',
]);

// Customers' words for the same thing. Each entry is a trimmed word (stem).
const RELATED: string[][] = [
  ['pay', 'paid', 'payout', 'charg', 'price', 'cost', 'money', 'refund', 'wallet', 'mobil'],
  ['cancel', 'refund'],
  ['book', 'reserv', 'appointment', 'job'],
  ['password', 'login', 'log', 'sign'],
  ['delet', 'remov', 'clos', 'deactivat'],
  ['chang', 'updat', 'edit', 'reschedul'],
  ['helper', 'cleaner', 'nanny', 'worker'],
  ['support', 'contact', 'person', 'help', 'agent'],
  ['verif', 'vet', 'check', 'safe', 'trust'],
  ['review', 'rating', 'rate'],
];

const SUFFIXES = ['ations', 'ation', 'ments', 'ment', 'ings', 'ing', 'ies', 'ied', 'es', 'ed', 's'];

// Forms that trimming an ending does not reach.
const IRREGULAR: Record<string, string> = { paid: 'pay', bought: 'buy', sent: 'send', lost: 'lose' };

/** A word with its ending trimmed: "payments" -> "pay", "charged" -> "charg". */
export function stem(word: string): string {
  let w = word.toLowerCase();
  if (IRREGULAR[w]) return IRREGULAR[w];
  for (let changed = true; changed;) {
    changed = false;
    for (const s of SUFFIXES) {
      if (w.endsWith(s) && w.length - s.length >= 3) {
        w = w.slice(0, -s.length);
        changed = true;
        break;
      }
    }
  }
  return w;
}

const words = (text: string) => text.toLowerCase().normalize('NFKD').match(/[a-z0-9]+/g) ?? [];
const topicWords = (text: string) => words(text).filter((w) => !STOP_WORDS.has(w) && w.length >= 2);

/** The query's topic words: each word's stem, and the stems related to it. */
function queryTerms(query: string): { own: string; related: string[] }[] {
  return topicWords(query).map((w) => {
    const own = stem(w);
    const related = RELATED.filter((group) => group.some((g) => own.startsWith(g) || (own.length >= 3 && g.startsWith(own))))
      .flat().filter((g) => g !== own);
    return { own, related: [...new Set(related)] };
  });
}

// A word matches the start of a longer one either way round ("cancel" and
// "cancellation"), but a short word only matches itself ("fee" is not
// "feedback").
const hit = (textStems: string[], t: string) =>
  textStems.some((w) => w === t || (t.length >= 4 && w.startsWith(t)) || (w.length >= 4 && t.startsWith(w)));

/**
 * The items that match the query, best first: the word itself before a
 * related one, the title before the answer, more matched words higher. An empty
 * query (or one of stop words only) returns every item, in order.
 */
export function searchHelp<T extends HelpItem>(items: T[], query: string): T[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return items;
  return items
    .map((item, index) => {
      const title = topicWords(item.question).map(stem);
      const body = topicWords(item.answer).map(stem);
      // The word itself counts more than a related one; the title more
      // than the answer.
      let score = 0;
      for (const { own, related } of terms) {
        if (hit(title, own)) score += 4;
        else if (hit(body, own)) score += 2;
        else if (related.some((r) => hit(title, r))) score += 2;
        else if (related.some((r) => hit(body, r))) score += 1;
      }
      return { item, index, score };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((r) => r.item);
}
